import { CourseSetDTO, CustomerDTO, LocationDTO, SiteDTO } from '@beable/roster-backend-client-v1';
import { Button, DialogActions, Divider, Grid, TextField, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import courseService from '../../../../services/CourseService';
import BaseDialog from '../../../Common/BaseDialog';
import CustomerSelector from '../../../Common/CustomerSelector';
import LocationSelector from '../../../Common/Selectors/LocationSelector';
import SiteSelector from '../../../Common/SiteSelector';
import CourseSelection, { CourseSetCourses } from './CourseSelection';
import GradeList from './GradeList';
import { getStyles } from './styles';

const DialogCustomerSelector = styled(CustomerSelector)(({ theme }) => ({
  margin: `${theme.spacing(1)} ${theme.spacing(2)}`,
  width: '100%',
  display: 'flex',
}));

const DialogSiteSelector = styled(SiteSelector)(({ theme }) => ({
  margin: `${theme.spacing(1)} ${theme.spacing(2)}`,
  width: '100%',
  display: 'flex',
}));

interface CourseSetDialogProps {
  open: boolean;
  handleClose: () => void;
  locationCode?: string;
  customerId?: string;
  siteId?: number;
  courseSet?: CourseSetDTO;
}

export default function CourseSetDialog({ open, handleClose, locationCode, customerId, siteId, courseSet }: CourseSetDialogProps) {
  const [name, setName] = useState<string>(courseSet?.name || '');
  const [grades, setGrades] = useState<string>();
  const [selectedCourses, setSelectedCourses] = useState<CourseSetCourses[]>(courseSet?.courses || []);
  const [selectedLocationCode, setSelectedLocationCode] = useState<string | undefined>(locationCode);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(customerId);
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(siteId);

  const theme = useTheme();
  const styles = getStyles(theme);

  const isValid = (): boolean => {
    if (!name || !selectedCourses.length) return false;
    return true;
  }

  const handleSave = async () => {
    let newCourseSet: CourseSetDTO = new CourseSetDTO();
    if (courseSet) newCourseSet = { ...courseSet };

    newCourseSet.name = name;
    newCourseSet.grades = grades;
    newCourseSet.locationCode = selectedLocationCode ?? undefined;
    newCourseSet.siteId = selectedSiteId ?? undefined;
    newCourseSet.courses = selectedCourses;

    const results = await courseService.saveCourseSet(newCourseSet)
    if (results) {
      alert(`${newCourseSet.name} saved successfully.`);
      onClose();
    } else {
      alert(`Error saving ${newCourseSet.name}. Please try again.`);
    }
  }

  const onClose = async () => {
    handleClose();
  }

  const DialogContent = () => {
    return (
      <>
        <Grid
          container
          sx={[{
            alignItems: "center"
          }, styles.courseSetGridContainer]}>
          <Grid size={1}>
          </Grid>
          <Grid size={2}>
            <Typography sx={styles.courseSetLabel}>Name</Typography>
          </Grid>
          <Grid size={8}>
            <TextField
              variant="outlined"
              onChange={(event) => setName(event.target.value)}
              value={name}
              fullWidth={true}
              size='small'
            />
          </Grid>
        </Grid>
        <Divider/>
        <Grid container sx={ styles.gridColumnContainer }>
            <Grid size={11}>
              <LocationSelector key='dialog-selector'
                locationCode={selectedLocationCode}
                onChange={(location: LocationDTO) => {setSelectedLocationCode(location ? location.isoCode : undefined)}}
                allOption
                variant='outlined'
              />
            </Grid>

            <Grid size={11}>
              <DialogCustomerSelector
                customerId={selectedCustomerId}
                locationCode={selectedLocationCode}
                onSelect={(customer: CustomerDTO) => setSelectedCustomerId(customer?.id)}
                variant='outlined'
                className='pFormControl'
                ignoreState
                noAutoSelect
              />
            </Grid>

            <Grid size={11}>
              <DialogSiteSelector
                siteId={selectedSiteId}
                onSelect={(site: SiteDTO) => setSelectedSiteId(site ? site.id : undefined)}
                customerId={selectedCustomerId ?? ''}
                disabled={!selectedCustomerId}
                variant='outlined'
                className='pFormControl'
                allOption
                ignoreState
              />
            </Grid>
        </Grid>
        <Divider/>
        <CourseSelection
          locationCode={selectedLocationCode ?? ''}
          courseSet={courseSet}
          handleSelectionChange={(selectedCourses) => setSelectedCourses(selectedCourses)}
        />
        <Divider/>
        <Grid
          container
          sx={[{ flexDirection: 'row' }, styles.courseSetGridContainer]}>
          <GradeList
            handleChange={(grades: string) => setGrades(grades)}
            grades={courseSet?.grades}
          />
        </Grid>
        <Divider/>
        <DialogActions>
          <Button
            variant='contained'
            color='primary'
            aria-label='save'
            onClick={handleSave}
            disabled={!isValid()}
          >
            Save
          </Button>
          <Button
            variant='contained'
            color='secondary'
            aria-label='cancel'
            onClick={handleClose}
          >
            Cancel
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <BaseDialog
      open={open}
      header={'Course Set Settings'}
      showCloseButton={false}
      showCloseIcon={true}
      onClose={onClose}
    >{
      DialogContent()
    }</BaseDialog>
  );
}
